import type { AgenticLogicSurface } from "../../../dtos/agentic-logic/surface";
import type {
  AgenticLogicItemIntent,
  AgenticLogicItemKind,
} from "../types/vocab";

// ---------------------------------------------------------------------------
// Per-surface SURFACE CAPABILITY — over item KINDS (what a surface can DRAW)
// and over item INTENTS (what a surface can DO). Both are declared ONCE here
// so the publish gate that refuses an unservable rule, the render path that
// would have drawn it, and the authoring contract the model reads agree by
// construction rather than by coincidence.
//
// The two halves live together on purpose: an author, a gate and a reader
// asking "can this surface handle this item" are asking one question, and
// splitting the answer across two files is how the second half gets forgotten.
// ---------------------------------------------------------------------------

/**
 * The values every surface in `surfaces` declares in `table` — the
 * intersection, never the union, with an EMPTY `surfaces` meaning "every
 * surface in the table". Shared by the kind and intent readers below so the
 * two tables can never be read by two different rules.
 */
function intersectSurfaceCapability<T extends string>(
  table: Readonly<Record<AgenticLogicSurface, readonly T[]>>,
  surfaces: readonly AgenticLogicSurface[]
): T[] {
  const lists =
    surfaces.length === 0
      ? Object.values(table)
      : surfaces.map((surface) => table[surface]);

  const [first, ...rest] = lists;
  return first.filter((value) => rest.every((list) => list.includes(value)));
}

/**
 * The item kinds each surface can actually DRAW today.
 *
 * This table is THE ONE STATEMENT of render capability.
 * `web/node-backend/src/lib/agentic-logic-surfaces/hydrate-items.ts` is a
 * CONSUMER of it, not a second owner: it used to hold a private
 * `HYDRATABLE_KINDS` const, which meant the publish path had no way to ask what
 * a surface could render and happily promoted rules onto permanently blank
 * placements.
 *
 * Everything absent from a surface's list — `image`, `reviewRef`,
 * `metaobjectRef`, `bundle` — is IGNORED at render rather than rejected: a
 * mixed envelope still renders its renderable half. What is refused is a rule
 * whose EVERY item is undrawable everywhere it can be placed.
 *
 * `satisfies` rather than a bare annotation so adding a surface to
 * {@link AgenticLogicSurfaceList} without stating its capability is a compile
 * error here rather than an `undefined` at gate time.
 */
export const AGENTIC_LOGIC_SURFACE_RENDERABLE_KINDS = {
  onsite: ["product", "variant"],
  checkout: ["product", "variant"],
  "post-purchase": ["product", "variant"],
} as const satisfies Record<
  AgenticLogicSurface,
  readonly AgenticLogicItemKind[]
>;

/**
 * Kinds renderable on EVERY surface in `surfaces` — the intersection, never the
 * union: a rule bound to two surfaces has to draw on both.
 *
 * An EMPTY `surfaces` means "every surface it could be placed on", for the same
 * reason {@link agenticLogicTightestSurfaceBudgetMs} prices an unassigned rule
 * at the TIGHTEST cap: a verdict reached now must not be invalidated by a later
 * assignment.
 */
export function agenticLogicRenderableKinds(
  surfaces: readonly AgenticLogicSurface[]
): AgenticLogicItemKind[] {
  return intersectSurfaceCapability(
    AGENTIC_LOGIC_SURFACE_RENDERABLE_KINDS,
    surfaces
  );
}

/**
 * The kinds in `kinds` that no such surface can draw — the unrenderable half,
 * de-duplicated and in first-seen order.
 */
export function agenticLogicUnrenderableKinds(
  kinds: readonly AgenticLogicItemKind[],
  surfaces: readonly AgenticLogicSurface[]
): AgenticLogicItemKind[] {
  const renderable = new Set(agenticLogicRenderableKinds(surfaces));
  const seen = new Set<AgenticLogicItemKind>();
  const unrenderable: AgenticLogicItemKind[] = [];
  for (const kind of kinds) {
    if (renderable.has(kind) || seen.has(kind)) {
      continue;
    }
    seen.add(kind);
    unrenderable.push(kind);
  }
  return unrenderable;
}

/**
 * The item INTENTS each surface can actually materialize today.
 *
 * `add` is the only one anywhere: a `replace` needs a cart transform that
 * removes the originating line, and no surface has one — checkout renders the
 * payload `POST /v2/checkout-blocks` hands it and can only offer an Add.
 *
 * Sibling of {@link AGENTIC_LOGIC_SURFACE_RENDERABLE_KINDS} and read the same
 * way: the publish gate that refuses an unmaterializable rule, the render path
 * that would have drawn it, and the authoring contract the model reads all
 * derive from THIS table, so they cannot drift. Wiring a cart transform onto a
 * surface is one cell here plus its tests — nothing else moves.
 *
 * `satisfies` rather than a bare annotation so adding a surface without stating
 * its intent capability is a compile error rather than an `undefined`.
 */
export const AGENTIC_LOGIC_SURFACE_SUPPORTED_INTENTS = {
  onsite: ["add"],
  checkout: ["add"],
  "post-purchase": ["add"],
} as const satisfies Record<
  AgenticLogicSurface,
  readonly AgenticLogicItemIntent[]
>;

/**
 * Intents supported on EVERY surface in `surfaces` — the intersection, never
 * the union, and an EMPTY `surfaces` means "every surface it could be placed
 * on", for the same reason {@link agenticLogicRenderableKinds} reads it that
 * way: a verdict reached now must not be invalidated by a later assignment.
 */
export function agenticLogicSupportedIntents(
  surfaces: readonly AgenticLogicSurface[]
): AgenticLogicItemIntent[] {
  return intersectSurfaceCapability(
    AGENTIC_LOGIC_SURFACE_SUPPORTED_INTENTS,
    surfaces
  );
}

/**
 * The intents in `intents` that no such surface can materialize, de-duplicated
 * and in first-seen order.
 *
 * `undefined` members are accepted DELIBERATELY: every caller maps over
 * `items.map((item) => item.intent)`, and "an omitted intent is `add`" is a
 * contract statement that must live in ONE place rather than at three call
 * sites. An absent intent is therefore never reported.
 */
export function agenticLogicUnsupportedIntents(
  intents: readonly (AgenticLogicItemIntent | undefined)[],
  surfaces: readonly AgenticLogicSurface[]
): AgenticLogicItemIntent[] {
  const supported = new Set(agenticLogicSupportedIntents(surfaces));
  const seen = new Set<AgenticLogicItemIntent>();
  const unsupported: AgenticLogicItemIntent[] = [];
  for (const declared of intents) {
    const intent = declared ?? "add";
    if (supported.has(intent) || seen.has(intent)) {
      continue;
    }
    seen.add(intent);
    unsupported.push(intent);
  }
  return unsupported;
}
